import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Typography } from '@mui/material';

import {
  resendTelegramCode,
  verifyTelegramEmail,
} from '@/shared/api/channels/telegramPersonal';
import { Box, Form, FormGenerator } from '@/shared/ui';
import { TFormMutation } from '@/shared/ui/Form';
import { FormFieldValues } from '@/shared/ui/FormGenerator';
import { Button } from '@/shared/ui/lib/Button';
import { CustomTypography } from '@/shared/ui/Typography';

import { telegramPersonalVerifyCodeDataSchema } from '../../model/schemas';
import { useTelegramAuthAction } from '../../model/telegramPersonal/useTelegramAuthAction';
import { useVerifySms } from '../../model/telegramPersonal/useVerifySms';
import { useTelegramAuth } from './TelegramAuthContext';

export const TELEGRAM_PERSONAL_VERIFY_CODE_FORM_ID =
  'telegram_personal_verify_code_form_id';

interface ITelegramPersonalVerifyCodeProps {
  mutation?: TFormMutation<void>;
  phone?: string;
  setIsCodeValid: Dispatch<SetStateAction<boolean>>;
  channelId?: string;
}

export const TelegramPersonalSMS = ({
  mutation,
  setIsCodeValid,
  phone = '',
  channelId = '',
}: ITelegramPersonalVerifyCodeProps) => {
  const { t } = useTranslation();
  const { authResult, applyAuthResult, setIsLoading } = useTelegramAuth();
  const { verifySms, isPending: isVerifying } = useVerifySms();
  const resend = useTelegramAuthAction<void>(() =>
    resendTelegramCode(channelId)
  );
  const verifyEmail = useTelegramAuthAction((data: { code: string }) =>
    verifyTelegramEmail(channelId, data)
  );
  const [now, setNow] = useState(Date.now);
  const codeLength = authResult?.code_length ?? 5;
  const schema = telegramPersonalVerifyCodeDataSchema(t, codeLength);
  const deadline = authResult?.resend_available_at
    ? Date.parse(authResult.resend_available_at)
    : 0;
  const secondsLeft = Math.max(0, Math.ceil((deadline - now) / 1000));
  const emailSetup = authResult?.step === 'email_setup_code';
  const delivery = authResult?.delivery ?? 'telegram';
  const fragmentUrl =
    delivery === 'fragment' &&
    authResult?.fragment_url?.startsWith('https://fragment.com/')
      ? authResult.fragment_url
      : undefined;

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    setIsCodeValid(false);
  }, [authResult, setIsCodeValid]);

  useEffect(() => {
    setIsLoading(isVerifying || verifyEmail.isPending || resend.isPending);
  }, [isVerifying, verifyEmail.isPending, resend.isPending, setIsLoading]);

  const handleSubmit = async ({ code }: { code: string }) => {
    const result = emailSetup
      ? await verifyEmail.mutateAsync({ code })
      : await verifySms({ phone, channelId, code });
    applyAuthResult(result);
  };

  const resendVerificationCode = async () => {
    applyAuthResult(await resend.mutateAsync());
    setNow(Date.now());
  };

  return (
    <Form
      key={`${authResult?.step}-${delivery}-${authResult?.resend_available_at ?? ''}`}
      id={TELEGRAM_PERSONAL_VERIFY_CODE_FORM_ID}
      schema={schema}
      onFieldChange={(data) => setIsCodeValid(schema.safeParse(data).success)}
      onSubmit={handleSubmit}
      mode="onChange"
      mutation={mutation}
    >
      <CustomTypography variant="body1" sx={{ mb: 2, display: 'inline-block' }}>
        {t(
          `channels:codeVerification.${emailSetup ? 'emailSetupCode' : `${delivery}Code`}`,
          {
            destination:
              delivery === 'email' ? (authResult?.email_pattern ?? '') : phone,
          }
        )}
      </CustomTypography>
      {fragmentUrl && (
        <Link
          href={fragmentUrl}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ display: 'block', mb: 2 }}
        >
          {t('channels:codeVerification.fragmentLink')}
        </Link>
      )}
      <FormGenerator
        formScheme={[
          {
            name: 'code',
            type: FormFieldValues.Default,
            inputProps: {
              sx: { maxWidth: '350px' },
              inputProps: { inputMode: 'numeric', maxLength: codeLength },
              autoComplete: 'one-time-code',
            },
          },
        ]}
      />
      {authResult?.can_resend && (
        <Box sx={{ mb: 1, mt: 2 }}>
          {secondsLeft > 0 ? (
            <Typography color="text.secondary">
              {t('channels:codeVerification.resendCodeIn')}
              {`${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')}`}
            </Typography>
          ) : (
            <Button
              onClick={resendVerificationCode}
              variant="text"
              size="small"
              loading={resend.isPending}
              disabled={isVerifying || verifyEmail.isPending}
            >
              {t('channels:codeVerification.resendCode')}
            </Button>
          )}
        </Box>
      )}
    </Form>
  );
};
